import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from "exceljs";
import { Grid as GridLoading } from "react-loader-spinner";
import Highlighter from "react-highlight-words";
import { styled } from "@mui/material/styles";
import { theme } from "../../theme";

// Import MUI elements
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import SquareIcon from "@mui/icons-material/Square";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CallIcon from "@mui/icons-material/Call";
import CategoryIcon from "@mui/icons-material/Category";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FeedbackIcon from "@mui/icons-material/Feedback";
import FlagIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { Divider } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: 13,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  height: 750,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const TranscriptModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1250,
  height: 750,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

function ComplaintsSearch({ parentToChild }: { parentToChild: any }) {
  const { startDate, endDate, mode, forceSearchInputValue } = parentToChild;

  const [selectedInsight, setSelectedInsight] = useState<any>(null);
  // const [insightsModalOpen, setInsightsModalOpen] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const [error, setError] = useState<any>(null);

  const [inputValue, setInputValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [selectedTranscript, setSelectedTranscript] = useState<[string, any[]]>(
    ["", []]
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [highlightsData, setHighlightsData] = useState<any>([]);

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("1");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [excelFile, setExcelFile] = useState<any | null>(null);
  const Today = dayjs();

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  // Handle excel file export
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Add headers
    const headers = Object.keys(data[0]);
    worksheet.addRow(headers);

    // Add data rows
    data.forEach((row: any) => {
      const rowData: any[] = []; // Explicitly define the type of rowData
      Object.values(row).forEach((cell) => {
        rowData.push(cell);
      });
      worksheet.addRow(rowData);
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();

    setExcelFile(excelBuffer);
  };

  const handlePreview = () => {
    const blob = new Blob([excelFile], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "preview.xlsx";
    a.click();

    URL.revokeObjectURL(url);
  };

  const fetchData = useCallback(async (data: any) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/new_search_complaints`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          keyword: data,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      setData(result);
      setIsLoading(false);
    } catch (err) {
      // setIsLoading(false);
      setError(err || "Something went wrong");
    }
  }, []);

  useEffect(() => {
    forceSearchInputValue && fetchData(inputValue);
  }, [flag]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    fetchData(inputValue);
  };

  const cleanTranscript = (transcript: string) => {
    let cleanTranscript = transcript.replace(/\*/g, "");

    // Find the index of the first occurrence of "Agent"
    let agentIndex = cleanTranscript.indexOf("Agent:");

    // If "Agent" is found, extract the substring starting from that index
    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
    }

    return finalTranscript;
  };

  const handleOpen = (row: any) => {
    let cleanTranscript = row["Transcript"].replace(/\*/g, "");

    // Find the index of the first occurrence of "Agent"
    let agentIndex = cleanTranscript.indexOf("Agent:");

    // If "Agent" is found, extract the substring starting from that index
    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
    }

    const transcriptArray = finalTranscript
      .split(/(?=Agent:|Customer:)/)
      .map((segment: string, index: number) => {
        if (index % 2 === 0) {
          return segment.trim();
        } else {
          return `\n${segment}`;
        }
      });

    // Remove the first element of the array if it starts with '[Agent]'
    if (transcriptArray[0] === "[Agent]") {
      transcriptArray.shift();
    }

    // console.log(row)
    setSelectedTranscript([row["Transcript ID"], transcriptArray]);

    // Set highlights data
    const complaintArray = [];
    const concernFlagArray = [];
    const dissatisfiedArray = [];

    row["Complaint Sentence"] !== "NULL"
      ? complaintArray.push(row["Complaint Sentence"])
      : complaintArray.push("");
    row["Concern Flag Text"] !== null
      ? concernFlagArray.push(row["Concern Flag Text"])
      : concernFlagArray.push("");
    row["Customer Dissatisfied Sentence"] !== "NULL"
      ? dissatisfiedArray.push(row["Customer Dissatisfied Sentence"])
      : dissatisfiedArray.push("");

    setHighlightsData([complaintArray, concernFlagArray, dissatisfiedArray]);

    setOpen(true);
  };

  const Highlight = ({
    children,
    highlightIndex,
  }: {
    children: React.ReactNode;
    highlightIndex: number;
  }) => {
    const colors = [
      "#ffe404", // Complaint
      "#907c1c", // Concern flag
      "#c4c4cd", // Dissatisfied Event
    ];
    const fontColors = ["black", "white", "black"];
    const color = colors[highlightIndex % colors.length];
    const fontColor = fontColors[highlightIndex % fontColors.length];
    return (
      <span
        style={{
          backgroundColor: color,
          fontFamily: theme.fonts.primary,
          color: fontColor,
        }}
      >
        {children}
      </span>
    );
  };

  const handleClose = () => {
    setOpen(false);
    setExcelFile(null);
  };

  const handleInsightsButtonClick = async (data: string) => {
    setSelectedInsight(data);
    setOpen(false);
  };

  const [groupInsightsData, setGroupInsightsData] = useState<any>(null);
  const [transcriptIds, setTranscriptIds] = useState<any>([]);
  const [groupInsightsModalOpen, setGroupInsightsModalOpen] =
    useState<boolean>(false);
  const [isGroupInsightsLoading, setIsGroupInsightsLoading] =
    useState<boolean>(true);

  const getTranscriptIds = () => {
    const ids = data.map((row: any) => row["Transcript ID"]);
    setTranscriptIds(ids);
  };
  const [customerIntentsData, setCustomerIntentsData] = useState<any>();
  const [generalSummaryData, setGeneralSummaryData] = useState<any>();
  const [primaryComplaintsData, setPrimaryComplaintsData] = useState<any>();
  const [productsData, setProductsData] = useState<any>();
  const [rootCausesData, setRootCausesData] = useState<any>();
  const [secondaryComplaintsData, setSecondaryComplaintsData] = useState<any>();

  // Get group insights data
  useEffect(() => {
    setIsGroupInsightsLoading(true);
    // console.log(transcriptIds);
    const fetchData = async () => {
      try {
        const response = await fetch(`/complaints_group_insights`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            transcript_ids: transcriptIds,
          }),
        });

        const result = await response.json();
        const resultArray = [Object.values(await result)];
        setGroupInsightsData(resultArray);

        const customerIntents = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "customer intents"
        )?.[1];

        const generalSummary = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "general summary of calls"
        )?.[1];
        const primaryComplaints = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "primary complaints"
        )?.[1];
        const products = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "products"
        )?.[1];
        const rootCauses = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "root causes"
        )?.[1];

        const secondaryComplaints = Object.entries(result).find(
          ([key, value]) => key.toLowerCase() === "secondary complaints"
        )?.[1];

        setCustomerIntentsData(customerIntents);
        setGeneralSummaryData(generalSummary);
        setPrimaryComplaintsData(primaryComplaints);
        setProductsData(products);
        setRootCausesData(rootCauses);
        setSecondaryComplaintsData(secondaryComplaints);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (transcriptIds.length > 0) {
      fetchData()
        .then(() => {
          setIsGroupInsightsLoading(false);
        })
        .catch((error) => console.error("Error in useEffect:", error));
    }
    // eslint-disable-next-line
  }, [transcriptIds]);

  // Send report button modal
  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Insights buttons modal
  // const handleCloseInsightsModal = () => setInsightsModalOpen(false);
  const handleCloseInsightsModal = () => setSelectedInsight(null);

  const handleCloseGroupInsightsModal = () => setGroupInsightsModalOpen(false);

  useEffect(() => {
    var init = "";
    fetchData(init);
  }, [fetchData, startDate, endDate]);

  error !== null && console.log(error);

  useEffect(() => {
    setInputValue(forceSearchInputValue);
    setFlag((flag) => !flag);
  }, [forceSearchInputValue]);

  // Function to manually parse and clean up the Next Steps string
  const parseNextSteps = (nextStepsString: string | null) => {
    if (!nextStepsString) return []; // If null or empty, return an empty array

    try {
      // Remove unnecessary characters like '['', '"]'
      const cleanedString = nextStepsString
        .replace(/[\[\]'"]/g, "") // Remove brackets and quotes
        .trim();

      // Split the cleaned string based on numbered steps (1., 2., etc.)
      const stepsArray = cleanedString
        .split(/\d+\.\s/)
        .filter((step) => step.trim() !== "");

      // Remove trailing commas from each step
      const formattedSteps = stepsArray.map((step) =>
        step.replace(/,\s*$/, "").trim()
      );

      return formattedSteps;
    } catch (error) {
      console.error("Failed to parse Next Steps:", error);
      return []; // Return an empty array if parsing fails
    }
  };

  const nextSteps = selectedInsight
    ? parseNextSteps(selectedInsight["Next Steps"])
    : [];

  return (
    <div className='ops-search-wrapper'>
      <div className='exec-search'>
        <Box component='form' noValidate autoComplete='off'>
          <TextField
            className='search-form'
            id='outlined-basic'
            label='Type your text here'
            variant='outlined'
            type='text'
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSubmit(e);
              }
            }}
          />
          <IconButton
            size='small'
            onClick={(e) => {
              handleSubmit(e);
            }}
          >
            <SearchIcon fontSize='inherit' />
          </IconButton>
        </Box>
        <div className='ops-search-buttons'>
          <Button
            className='search-button'
            variant='contained'
            endIcon={<SendIcon />}
            onClick={() => {
              handleExport();
              openModal();
            }}
          >
            Send Report
          </Button>
          {data && data.length > 0 && !isLoading && (
            <Button
              className='search-button'
              variant='contained'
              endIcon={<AutoAwesomeIcon />}
              onClick={() => {
                getTranscriptIds();
                setGroupInsightsModalOpen(true);
              }}
            >
              AI Group Insights
            </Button>
          )}
        </div>
      </div>
      {data && !isLoading && data.length > 0 ? (
        <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
          <Table
            sx={{ minWidth: 500 }}
            stickyHeader
            aria-label='sticky table'
            id='myTable'
          >
            <TableHead>
              <TableRow>
                <StyledTableCell className='table-header' align='center'>
                  Transcript ID
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  L1 Category
                  {mode === "dark" ? (
                    <span className='shine'>
                      <AutoAwesomeIcon />
                    </span>
                  ) : (
                    <span className='ai-driven'>
                      <AutoAwesomeIcon />
                    </span>
                  )}
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  L2 Category
                  {mode === "dark" ? (
                    <span className='shine'>
                      <AutoAwesomeIcon />
                    </span>
                  ) : (
                    <span className='ai-driven'>
                      <AutoAwesomeIcon />
                    </span>
                  )}
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  Resolved
                  {mode === "dark" ? (
                    <span className='shine'>
                      <AutoAwesomeIcon />
                    </span>
                  ) : (
                    <span className='ai-driven'>
                      <AutoAwesomeIcon />
                    </span>
                  )}
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  Transcript
                  <span style={{ fontSize: 10 }}> (Click to view)</span>
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  Sentiment
                  {mode === "dark" ? (
                    <span className='shine'>
                      <AutoAwesomeIcon />
                    </span>
                  ) : (
                    <span className='ai-driven'>
                      <AutoAwesomeIcon />
                    </span>
                  )}
                  <br />
                  <span style={{ fontSize: 10 }}>1 - 10</span>
                </StyledTableCell>
                <StyledTableCell className='table-header' align='center'>
                  Additional Insights
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row: any, index: number) => (
                <StyledTableRow key={index}>
                  <StyledTableCell style={{ width: "7%" }} align='center'>
                    {row["Transcript ID"]}
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "10%" }} align='center'>
                    {row["L1 Category"]}
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "10%" }} align='center'>
                    {row["L2 Category"]}
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "8%" }} align='center'>
                    {row["Resolved"]}
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "22%" }} align='center'>
                    <div
                      className='table-transcript-link'
                      onClick={() => handleOpen(row)}
                    >
                      {cleanTranscript(row["Transcript"].substr(0, 200)) +
                        "..."}
                    </div>
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "10%" }} align='center'>
                    {row["Call Sentiment"]}
                  </StyledTableCell>
                  <StyledTableCell style={{ width: "15%" }} align='center'>
                    <Button
                      className='modal-btn'
                      variant='contained'
                      endIcon={<AutoAwesomeIcon />}
                      onClick={() => {
                        handleInsightsButtonClick(row);
                      }}
                    >
                      AI Insights
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : isLoading ? (
        <div className='search-loading-area'>
          <Skeleton width='100%' height='200px' />
          <span className='centered-text'>
            <GridLoading
              visible={true}
              height='15'
              width='15'
              color={mode === "dark" ? "white" : "black"}
              ariaLabel='grid-loading'
              radius='12.5'
              wrapperStyle={{}}
              wrapperClass='grid-wrapper'
            />
            Retrieving records...
          </span>
        </div>
      ) : (
        data &&
        data.length === 0 && (
          <div className='no-records-found'>
            <span>No records found</span>
          </div>
        )
      )}

      <Modal
        open={groupInsightsModalOpen}
        onClose={handleCloseGroupInsightsModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        className='modal-global'
      >
        <Box className='modal-style' sx={ModalStyle}>
          <div className='modal-header'>
            <h3>
              <AutoAwesomeIcon /> AI Group Insights
            </h3>
            <Button
              className='modal-btn'
              variant='contained'
              onClick={handleCloseGroupInsightsModal}
            >
              Close
            </Button>
          </div>
          <div id='modal-modal-description'>
            {!isGroupInsightsLoading &&
            groupInsightsData &&
            groupInsightsData[0].length === 6 &&
            groupInsightsData[0][0].length > 0 ? (
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2} className='modal-summary-notes'>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <FeedbackIcon />
                          Common Products
                        </h4>
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {productsData ? (
                            productsData
                              .slice(0, 4)
                              .map((value: any, index: number) => (
                                <span key={index}>{value}</span>
                              ))
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <FeedbackIcon />
                          Complaints Reasons
                        </h4>
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {primaryComplaintsData ? (
                            primaryComplaintsData.map(
                              (value: any, index: number) => (
                                <span key={index}>{value}</span>
                              )
                            )
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <CallIcon />
                          Specific Complaints
                        </h4>
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {secondaryComplaintsData ? (
                            secondaryComplaintsData.map(
                              (value: any, index: number) => (
                                <span key={index}>{value}</span>
                              )
                            )
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <CheckCircleIcon />
                          Customer Intents
                        </h4>
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {customerIntentsData ? (
                            customerIntentsData.map(
                              (value: any, index: number) => (
                                <span key={index}>{value}</span>
                              )
                            )
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <CheckCircleIcon />
                          Root Causes
                        </h4>
                        <div
                          style={{
                            textAlign: "left",
                            display: "flex",
                            flexDirection: "column",
                            // alignItems: "center",
                            gap: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {rootCausesData ? (
                            rootCausesData.map((value: any, index: number) => (
                              <li key={index}>{value}</li>
                            ))
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card
                      variant='outlined'
                      className='get-insights-card group-insights'
                    >
                      <CardContent>
                        <h4>
                          <CheckCircleIcon />
                          General Summary of Calls
                        </h4>
                        <div
                          style={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          {generalSummaryData ? (
                            <span>{generalSummaryData}</span>
                          ) : (
                            <span>No data available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <div className='position-reletive'>
                <Skeleton width='100%' height='450px' />
                <span className='centered-text'>
                  <GridLoading
                    visible={true}
                    height='15'
                    width='15'
                    color={mode === "dark" ? "white" : "black"}
                    ariaLabel='grid-loading'
                    radius='12.5'
                    wrapperStyle={{}}
                    wrapperClass='grid-wrapper'
                  />
                  AI is generating group insights...
                </span>
              </div>
            )}
          </div>
        </Box>
      </Modal>

      {selectedTranscript[1].length > 0 && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={TranscriptModalStyle}>
            <div className='modal-header'>
              <h3>
                <SummarizeIcon /> Full Transcript
              </h3>
              <Button
                className='modal-btn'
                variant='contained'
                endIcon={<AutoAwesomeIcon />}
                onClick={() => {
                  handleInsightsButtonClick(
                    data?.find(
                      (item: any) =>
                        item["Transcript ID"] === selectedTranscript[0]
                    )
                  );
                  // handleInsightsButtonClick(selectedTranscript[0]);
                }}
              >
                AI Insights
              </Button>
            </div>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Card variant='outlined' className='get-insights-card'>
                    <CardContent>
                      <Typography
                        id='modal-modal-description transcript-show-modal'
                        sx={{ mt: 2 }}
                      >
                        {selectedTranscript[1].map((segment, index) => (
                          <React.Fragment key={index}>
                            <Highlighter
                              highlightClassName='highlighted-sentence'
                              searchWords={highlightsData.flat()}
                              autoEscape={true}
                              caseSensitive={true}
                              textToHighlight={segment}
                              highlightTag={(props: {
                                children: React.ReactNode;
                              }) => (
                                <Highlight
                                  {...props}
                                  highlightIndex={highlightsData.findIndex(
                                    (highlight: string) =>
                                      highlight.includes(
                                        props.children as string
                                      )
                                  )}
                                />
                              )}
                            />
                            <br />
                            <br />
                          </React.Fragment>
                        ))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {/* <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                  <Card
                    variant='outlined'
                    className='complaints-legend-card highlight-legend'
                  >
                    {highlightsData && highlightsData.length > 0 ? (
                      <CardContent>
                        <div className='complaints-highligting-right'>
                          <div className='legend-right'>
                            <p>
                              <SquareIcon className='color-oc' /> Complaint
                              Mentioned
                            </p>
                            <span>
                              {highlightsData[0][0] === "NULL" ||
                              highlightsData[0][0] === undefined ||
                              highlightsData[0][0] === "" ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <Divider />
                          <div className='legend-right'>
                            <p>
                              <SquareIcon className='color-caller' /> Concern
                              Flag
                            </p>
                            <span>
                              {highlightsData[1][0] === "NULL" ||
                              highlightsData[1][0] === undefined ||
                              highlightsData[1][0] === "" ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <Divider />
                          <div className='legend-right'>
                            <p>
                              <SquareIcon className='color-theme' />{" "}
                              Dissatisfaction Event
                            </p>
                            <span>
                              {highlightsData[2][0] === "NULL" ||
                              highlightsData[2][0] === undefined ||
                              highlightsData[2][0] === "" ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      <Skeleton width='100%' height='100%' />
                    )}
                  </Card>
                </Grid> */}
              </Grid>
            </Box>
          </Box>
        </Modal>
      )}
      <Modal
        className='modal-global'
        // open={insightsModalOpen}
        open={!!selectedInsight}
        onClose={handleCloseInsightsModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className='modal-style' sx={ModalStyle}>
          <div className='modal-header'>
            <h3>
              <AutoAwesomeIcon /> AI Insights
            </h3>
            <Button
              className='modal-btn'
              variant='contained'
              onClick={handleCloseInsightsModal}
            >
              Close
            </Button>
          </div>
          <div id='modal-modal-description'>
            {!isLoading ? (
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2} className='modal-summary-notes'>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SentimentNeutralIcon />
                          Call Sentiment
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["Call Sentiment"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <FlagIcon />
                          Product
                        </h4>
                        <span style={{ textTransform: "capitalize" }}>
                          {selectedInsight
                            ? selectedInsight["Products"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SentimentNeutralIcon />
                          Complaint Reason
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["L1 Category"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <FlagIcon />
                          Complaint Sub-Reason
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["L2 Category"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SentimentNeutralIcon />
                          User Intent
                        </h4>
                        {selectedInsight
                          ? selectedInsight["L3 Category"]
                          : "No data"}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <FlagIcon />
                          Root Cause
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["Root Cause"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <FeedbackIcon />
                          Root Cause Snippet
                        </h4>
                        <span>
                          {selectedInsight &&
                          selectedInsight["Root Cause Snippet"]
                            ? selectedInsight["Root Cause Snippet"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SummarizeIcon />
                          Resolved
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["Resolved"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <SummarizeIcon />
                          Summary
                        </h4>
                        <span>
                          {selectedInsight
                            ? selectedInsight["Call Summary"]
                            : "No data"}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className='summary-card'
                  >
                    <Card variant='outlined' className='get-insights-card'>
                      <CardContent>
                        <h4>
                          <FlagIcon />
                          Next Steps
                        </h4>
                        <span>
                          {nextSteps.length > 0 ? (
                            <ol>
                              {nextSteps.map((step: string, index: number) => (
                                <li key={index} style={{ textAlign: "left" }}>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          ) : (
                            "No data"
                          )}
                        </span>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <div className='position-reletive'>
                <Skeleton width='100%' height='450px' />
                <span className='centered-text'>
                  <GridLoading
                    visible={true}
                    height='15'
                    width='15'
                    color={mode === "dark" ? "white" : "black"}
                    ariaLabel='grid-loading'
                    radius='12.5'
                    wrapperStyle={{}}
                    wrapperClass='grid-wrapper'
                  />
                  AI is generating insights...
                </span>
              </div>
            )}
          </div>
        </Box>
      </Modal>

      <Modal
        className='exec-send-modal modal-global'
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className='modal-style' sx={ModalStyle}>
          <h3>
            <SendIcon /> Report Form
          </h3>
          <div>
            <TextField
              label='Subject'
              variant='outlined'
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              margin='normal'
            />
            <TextField
              label='Email'
              variant='outlined'
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin='normal'
            />
            <TextField
              label='Additional Comments'
              variant='outlined'
              fullWidth
              multiline
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              margin='normal'
            />
            <FormControl fullWidth className='send-report-dropdown'>
              <InputLabel id='select-label'>Report Frequency</InputLabel>
              <Select
                labelId='select-label'
                id='select'
                value={frequency}
                onChange={(e) => {
                  setFrequency(e.target.value);
                }}
              >
                <MenuItem value='Daily'>Daily</MenuItem>
                <MenuItem value='Weekly'>Weekly</MenuItem>
                <MenuItem value='Monthly'>Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label='Num of Recurrences'
              variant='outlined'
              fullWidth
              type='number'
              value={recurrences}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 0 && value <= 365) {
                  setRecurrences(value.toString());
                } else if (e.target.value === "") {
                  setRecurrences("");
                }
              }}
              margin='normal'
              inputProps={{ inputMode: "numeric", max: 365 }}
            />
            <p className='text-dark-mode'>Recurrence Range:</p>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
              <div className='send-report-datepickers'>
                <DatePicker
                  className='exec-date-picker'
                  label='Start'
                  value={rangeStartDate}
                  onChange={(date) => {
                    if (date && date.isBefore(Today, "day")) {
                      setRangeStartDate(Today);
                    } else {
                      setRangeStartDate(date || Today);
                    }
                  }}
                />
                <span className='text-dark-mode'> to </span>
                <DatePicker
                  className='exec-date-picker'
                  label='End'
                  value={rangeEndDate}
                  onChange={(date) => {
                    if (date && date.isBefore(Today, "day")) {
                      setRangeEndDate(Today);
                    } else if (
                      rangeStartDate &&
                      date &&
                      date.isBefore(rangeStartDate, "day")
                    ) {
                      setRangeEndDate(rangeStartDate);
                    } else {
                      setRangeEndDate(date || Today);
                    }
                  }}
                />
              </div>
            </LocalizationProvider>
            {excelFile && (
              <div className='table-file-export'>
                <p onClick={handlePreview}>Preview Excel File</p>
                {/* <input type="hidden" name="data" value={excelFile} /> */}
              </div>
            )}
          </div>
          <div>
            <Button
              className='exec-send-btn'
              variant='contained'
              onClick={handleSendReport}
            >
              Send
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default ComplaintsSearch;
